import { NavLink } from 'react-router-dom'
import { Shield, BarChart3, Upload, Home, X, PanelLeftClose } from 'lucide-react'
import { Button } from '@/components/ui'
import { getAppVersion } from '@/lib/version'
import { useState } from 'react'

interface NavItem {
  name: string
  path: string
  icon: React.ComponentType<{ className?: string }>
  end?: boolean // Use exact matching for this route
}

interface NavGroup {
  title: string
  items: NavItem[]
}

const navigation: NavGroup[] = [
  {
    title: 'Overview',
    items: [
      { name: 'Dashboard', path: '/', icon: Home, end: true },
    ],
  },
  {
    title: 'Data Privacy',
    items: [
      { name: 'Privacy Subjects', path: '/privacy', icon: Shield, end: true },
      { name: 'Statistics', path: '/privacy/stats', icon: BarChart3 },
      { name: 'Import CSV', path: '/privacy/import', icon: Upload },
    ],
  },
]

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false)

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50
          bg-white dark:bg-slate-800
          border-r border-slate-200 dark:border-slate-700
          transform transition-all duration-200 ease-in-out
          lg:translate-x-0 lg:static lg:z-auto
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isCollapsed ? 'w-16' : 'w-64'}
        `}
      >
        <div className="h-full flex flex-col">
          {/* Mobile close button */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200 dark:border-slate-700 lg:hidden">
            <span className="font-semibold text-slate-900 dark:text-white">Menu</span>
            <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close menu">
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation - scrollable */}
          <nav className="flex-1 overflow-y-auto py-4 scrollbar-thin">
            {navigation.map((group) => (
              <div key={group.title} className="px-3 mb-6">
                {!isCollapsed && (
                  <h3 className="px-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    {group.title}
                  </h3>
                )}
                <ul className="space-y-1">
                  {group.items.map((item) => (
                    <li key={item.path}>
                      <NavLink
                        to={item.path}
                        end={item.end}
                        onClick={() => onClose()}
                        className={({ isActive }) => `
                          flex items-center gap-3 px-3 py-2 rounded-md
                          text-sm font-medium
                          transition-colors duration-150
                          ${isCollapsed ? 'justify-center' : ''}
                          ${
                            isActive
                              ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                              : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                          }
                        `}
                        title={isCollapsed ? item.name : undefined}
                      >
                        <item.icon className="h-5 w-5 flex-shrink-0" />
                        {!isCollapsed && item.name}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          {/* Footer - pinned to bottom */}
          <div className="flex-shrink-0 border-t border-slate-200 dark:border-slate-700">
            {/* Version display */}
            {!isCollapsed && (
              <div className="px-3 pt-3 pb-2">
                <p className="text-xs text-slate-500 dark:text-slate-400 text-center font-mono">
                  {getAppVersion()}
                </p>
              </div>
            )}

            {/* Collapse toggle - desktop only */}
            <div className="p-3">
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="hidden lg:flex items-center justify-center w-full p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors"
                title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <PanelLeftClose
                  className={`h-4 w-4 transition-transform ${isCollapsed ? 'rotate-180' : ''}`}
                />
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  )
}
